import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Card } from "./ui/Card";
import Spinner from "./ui/Spinner";
import fetchWithCredentials, {
  isPresignedUrl,
  isPresignedUrlExpired,
  normalizeFileUrls,
  refreshPresignedFileUrl,
  regenerateFileUrl,
} from "apiClient";
import { CondoCaseUpdate } from "types/CondoCase";
import { formatTimestamp } from "utils/dateFormat";
import { useAuth } from "context/AuthContext";

interface CaseUpdatesProps {
  caseId: number;
  embedded?: boolean;
}

export function CaseUpdates({ caseId, embedded = false }: CaseUpdatesProps) {
  const [updates, setUpdates] = useState<CondoCaseUpdate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const tr = useCallback(
    (es: string, en: string) => (locale === "es" ? es : en),
    [locale],
  );

  useEffect(() => {
    const fetchUpdates = async () => {
      setLoading(true);
      setError(null);
      try {
        const updatesData: CondoCaseUpdate[] = await fetchWithCredentials(
          `/manager-api/cases/${caseId}/updates/`
        );
        setUpdates(
          updatesData.map((update) => ({
            ...update,
            fileUrls: normalizeFileUrls(update.fileUrls),
          })),
        );
      } catch (err: any) {
        console.error(err);
        setError(
          err.message ||
            tr(
              "Error al cargar las actualizaciones del incidente",
              "Failed to load incident updates",
            ),
        );
      } finally {
        setLoading(false);
      }
    };

    if (caseId) {
      fetchUpdates();
    }
  }, [caseId, tr]);

  if (loading) {
    return (
      <div className={`flex justify-center ${embedded ? "p-4" : ""}`}>
        {embedded ? (
          <Spinner />
        ) : (
          <Card className="flex w-full justify-center p-4">
            <Spinner />
          </Card>
        )}
      </div>
    );
  }

  if (error) {
    return embedded ? (
      <div className="text-sm text-red-500">{error}</div>
    ) : (
      <Card className="p-4">
        <div className="text-sm text-red-500">{error}</div>
      </Card>
    );
  }

  if (updates.length === 0) {
    const emptyMessage = tr(
      "No hay actualizaciones disponibles",
      "No updates available",
    );
    return embedded ? (
      <div className="text-sm text-gray-500">{emptyMessage}</div>
    ) : (
      <Card className="p-4">
        <div className="text-sm text-gray-500">{emptyMessage}</div>
      </Card>
    );
  }

  const updatesList = (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
      {updates.map((update) => (
        <div
          key={update.id}
          className="rounded-r border-l-4 border-blue-500 bg-white py-2 pl-4"
        >
          <div className="mb-1 text-xs text-gray-500">
            {formatTimestamp(update.createdAt, locale)}
          </div>
          <div className="whitespace-pre-wrap text-sm text-gray-800">
            {update.content}
          </div>
          {update.fileUrls.length > 0 && (
            <UpdateFileAttachments fileUrls={update.fileUrls} locale={locale} />
          )}
        </div>
      ))}
    </div>
  );

  if (embedded) {
    return (
      <div
        className="flex h-full min-h-0 flex-col"
        style={{ backgroundColor: "#FEF4E8" }}
      >
        {updatesList}
      </div>
    );
  }

  return (
    <Card
      className="flex h-full min-h-0 flex-col gap-4 p-4"
      style={{ backgroundColor: "#FEF4E8" }}
    >
      <h3 className="shrink-0 text-lg font-semibold text-gray-800">
        {tr("Actualizaciones", "Updates")}
      </h3>
      {updatesList}
    </Card>
  );
}

function UpdateFileAttachments({
  fileUrls,
  locale,
}: {
  fileUrls: string[];
  locale: string;
}) {
  const tr = (es: string, en: string) => (locale === "es" ? es : en);
  const fileFallbackLabel = tr("Archivo adjunto", "File attachment");
  const fileExtFallbackLabel = tr("ARCHIVO", "FILE");

  return (
    <div className="mt-2 space-y-2">
      {fileUrls.map((url) => (
        <UpdateFileAttachment
          key={url}
          url={url}
          fileFallbackLabel={fileFallbackLabel}
          fileExtFallbackLabel={fileExtFallbackLabel}
          tr={tr}
        />
      ))}
    </div>
  );
}

function UpdateFileAttachment({
  url,
  fileFallbackLabel,
  fileExtFallbackLabel,
  tr,
}: {
  url: string;
  fileFallbackLabel: string;
  fileExtFallbackLabel: string;
  tr: (es: string, en: string) => string;
}) {
  const [fileUrl, setFileUrl] = useState(url);
  const [regeneratingUrl, setRegeneratingUrl] = useState(false);

  useEffect(() => {
    let cancelled = false;
    refreshPresignedFileUrl(url).then((freshUrl) => {
      if (!cancelled) {
        setFileUrl(freshUrl);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const regenerateAndUpdateUrl = async (oldUrl: string) => {
    if (!oldUrl || regeneratingUrl) {
      return;
    }
    setRegeneratingUrl(true);
    try {
      const newUrl = await regenerateFileUrl(oldUrl);
      if (newUrl) {
        setFileUrl(newUrl);
      }
    } catch (err) {
      console.error("Failed to regenerate file URL:", err);
    } finally {
      setRegeneratingUrl(false);
    }
  };

  const handleImageError = async () => {
    if (!isPresignedUrl(fileUrl)) {
      return;
    }
    await regenerateAndUpdateUrl(fileUrl);
  };

  const handleDownloadClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (!fileUrl) {
      e.preventDefault();
      return;
    }

    if (!isPresignedUrl(fileUrl) || !isPresignedUrlExpired(fileUrl)) {
      return;
    }

    e.preventDefault();
    setRegeneratingUrl(true);
    try {
      const targetUrl = await refreshPresignedFileUrl(fileUrl);
      setFileUrl(targetUrl);
      const link = document.createElement("a");
      link.href = targetUrl;
      link.download = getFileName(targetUrl, fileFallbackLabel);
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to regenerate URL for download:", err);
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } finally {
      setRegeneratingUrl(false);
    }
  };

  if (!fileUrl.trim()) {
    return null;
  }

  if (isImageUrl(fileUrl)) {
    return (
      <div className="w-full">
        <img
          src={fileUrl}
          alt={getFileName(fileUrl, fileFallbackLabel)}
          className="rounded-md w-full h-auto max-w-full object-contain"
          style={{ maxHeight: "300px" }}
          onError={handleImageError}
        />
        {regeneratingUrl && (
          <div className="text-xs text-gray-500 mt-1">
            {tr("Regenerando URL...", "Regenerating URL...")}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 bg-gray-100 rounded p-2 max-w-full">
      <span className="font-semibold bg-gray-200 rounded px-2 py-0.5 text-xs flex-shrink-0">
        {getFileExtension(fileUrl, fileExtFallbackLabel, fileFallbackLabel)}
      </span>
      <span className="text-xs truncate flex-1">
        {getFileName(fileUrl, fileFallbackLabel)}
      </span>
      <a
        href={fileUrl}
        download={getFileName(fileUrl, fileFallbackLabel)}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center text-blue-600 hover:underline flex-shrink-0"
        title={tr("Descargar", "Download")}
        onClick={handleDownloadClick}
      >
        <Download className="h-4 w-4" />
      </a>
    </div>
  );
}

function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(url.split("?")[0]);
}

function getFileName(url: string, fallbackLabel: string): string {
  try {
    return decodeURIComponent(
      url.split("/").pop()?.split("?")[0] || fallbackLabel,
    );
  } catch {
    return fallbackLabel;
  }
}

function getFileExtension(
  url: string,
  fallbackExtensionLabel: string,
  fallbackFileNameLabel: string,
): string {
  const name = getFileName(url, fallbackFileNameLabel);
  const match = name.match(/\.([a-zA-Z0-9]+)$/);
  return match ? match[1].toUpperCase() : fallbackExtensionLabel;
}
